// SPDX-License-Identifier: MIT
// The pre-magnify discovery seam. `welcome.ts` drives a multi-turn Q&A through
// this interface BEFORE any project exists (converse/discover both require a
// saved projectId, so neither fits here). v1 = GuidedDiscussion: a curated,
// lightly-adaptive question sequence, client-side, no network — the real
// intelligence stays in the final magnify, which receives the transcript via
// `attachments_context` (same shape as discover.ts:buildRefinementContext).
// A future EdgeDiscussion (new `discuss` edge fn, per-turn Claude) can implement
// this same interface and be dropped in at the call site with no UI rework.
import type { Locale } from '../i18n/detect';
import { t } from '../i18n/t';

export interface DiscussionTurn {
  reply: string; // Genesis's chat bubble for this turn (usually an ack + the next question)
  ready: boolean; // true once enough answers exist to magnify a solid sheet
}

export interface DiscussionState {
  idea: string;
  exchanges: { q: string; a: string }[]; // answered question text + the user's answer
  pendingQuestionId?: string; // the question currently shown (awaiting an answer)
  askedIds: string[]; // question ids already posed (avoids repeats)
  clarifiedIds: string[]; // ids for which a one-shot clarifier has already been issued
}

export interface IdeaDiscussion {
  start(idea: string, locale: Locale): { state: DiscussionState; turn: DiscussionTurn };
  respond(state: DiscussionState, userMessage: string, locale: Locale): { state: DiscussionState; turn: DiscussionTurn };
  toMagnifyContext(state: DiscussionState, locale: Locale): string;
}

export const READY_AFTER_ANSWERS = 2;

// ---------------------------------------------------------------------------
// Domain detection (keyless, pure, regex-based)
// ---------------------------------------------------------------------------

export type Domain = 'care' | 'commerce' | 'game' | 'tool' | 'content' | 'other';

// Unicode-aware word boundaries: `\b` does not work for accented characters
// (e.g. `santé`, `médical`) because JS `\w` excludes À-ÿ. Lookahead/lookbehind
// with the expanded letter class `[a-zA-ZÀ-ÖØ-öø-ÿ]` gives correct boundaries.
// Note: `[À-ÿ]` includes `×`(U+00D7) and `÷`(U+00F7); the split class avoids them.
const W = '(?<![a-zA-ZÀ-ÖØ-öø-ÿ])';
const w = '(?![a-zA-ZÀ-ÖØ-öø-ÿ])';
const DOMAIN_HINTS: { domain: Domain; re: RegExp }[] = [
  { domain: 'care',     re: new RegExp(`${W}(sant[eé]|parent|soin|patient|aidant|m[eé]dical|bien-?[eê]tre)${w}`, 'i') },
  { domain: 'commerce', re: new RegExp(`${W}(vendre|boutique|commande|client|paiement|panier|stock|facture)${w}`, 'i') },
  { domain: 'game',     re: new RegExp(`${W}(jeu|game|joueur|niveau|score|minecraft|quiz)${w}`, 'i') },
  { domain: 'content',  re: new RegExp(`${W}(blog|article|cours|vid[eé]o|podcast|newsletter|portfolio)${w}`, 'i') },
  { domain: 'tool',     re: new RegExp(`${W}(outil|tableau|suivi|gestion|tracker|dashboard|organiser|planifier)${w}`, 'i') },
];

export function detectDomain(idea: string): Domain {
  for (const h of DOMAIN_HINTS) if (h.re.test(idea)) return h.domain;
  return 'other';
}

// A short noun-phrase pulled from the idea to weave into a question. Trimmed to
// keep the question readable; falls back to a generic word when the idea is bare.
function ideaSubject(idea: string): string {
  const cleaned = idea.trim().replace(/^(une?|le|la|les|des|un)\s+/i, '');
  const words = cleaned.split(/\s+/).slice(0, 4).join(' ');
  return words || 'ton projet';
}

export function phraseQuestion(id: string, idea: string, domain: Domain): string {
  const tmpl = t(`discoveryChat.q.${id}`); // may contain a literal "{idea}" slot
  const subject = ideaSubject(idea);
  // Function-replacer: a subject containing `$`/regex patterns must NOT be
  // reinterpreted by String.replace's special replacement syntax.
  const base = tmpl.includes('{idea}') ? tmpl.replace('{idea}', () => subject) : tmpl;
  // FIX I1: append a domain-contextual example hint for the data question.
  if (id === 'data' && domain !== 'other') {
    const hint = t(`discoveryChat.dataHint.${domain}`);
    return `${base} ${hint}`;
  }
  return base;
}

export function isVagueAnswer(answer: string): boolean {
  const words = answer.trim().split(/\s+/).filter(Boolean);
  return words.length <= 1;
}

// ---------------------------------------------------------------------------
// Ordered question ids; `skipIf` drops a question when the idea already answers
// it (keeps the chat short + relevant).
// ---------------------------------------------------------------------------

const QUESTIONS: { id: string; skipIf?: (idea: string) => boolean }[] = [
  { id: 'audience' },
  { id: 'core' },
  { id: 'platform', skipIf: (idea) => /\b(site|web|appli|app|mobile|desktop|bureau|application)\b/i.test(idea) },
  { id: 'data' },
  { id: 'constraint' },
];

function questionText(id: string, idea: string): string {
  return phraseQuestion(id, idea, detectDomain(idea));
}

function nextQuestionId(idea: string, askedIds: string[]): string | undefined {
  for (const q of QUESTIONS) {
    if (askedIds.includes(q.id)) continue;
    if (q.skipIf?.(idea)) continue;
    return q.id;
  }
  return undefined;
}

// Advances the state to the next pending question (or wrap-up) and returns the
// turn the chat should render. Mutates `state` in place (pendingQuestionId,
// askedIds) — callers thread the returned state forward.
function buildTurn(state: DiscussionState): DiscussionTurn {
  const answers = state.exchanges.length;
  const ready = answers >= READY_AFTER_ANSWERS;
  const nextId = nextQuestionId(state.idea, state.askedIds);
  if (!nextId) {
    state.pendingQuestionId = undefined;
    return { reply: t('discoveryChat.wrap'), ready: true };
  }
  state.pendingQuestionId = nextId;
  state.askedIds.push(nextId);
  // Acknowledge only after the first answer — the opening turn is just the
  // first question (the greeting is rendered separately by the component).
  const ack = answers > 0 ? `${t('discoveryChat.ack')} ` : '';
  return { reply: `${ack}${questionText(nextId, state.idea)}`, ready };
}

export function createGuidedDiscussion(): IdeaDiscussion {
  return {
    start(idea, _locale) {
      const state: DiscussionState = { idea: idea.trim(), exchanges: [], askedIds: [], clarifiedIds: [] };
      const turn = buildTurn(state);
      return { state, turn };
    },

    respond(state, userMessage, _locale) {
      const answer = userMessage.trim();
      // FIX I2: one-shot clarifier for vague answers (before ready threshold).
      if (
        state.pendingQuestionId &&
        isVagueAnswer(answer) &&
        !state.clarifiedIds.includes(state.pendingQuestionId) &&
        state.exchanges.length < READY_AFTER_ANSWERS
      ) {
        state.clarifiedIds.push(state.pendingQuestionId);
        // Do NOT record an exchange; keep the same pendingQuestionId.
        return { state, turn: { reply: t('discoveryChat.clarify'), ready: false } };
      }
      if (state.pendingQuestionId) {
        state.exchanges.push({ q: questionText(state.pendingQuestionId, state.idea), a: answer });
      } else {
        // Free-form message after wrap-up — keep it as extra signal for magnify.
        state.exchanges.push({ q: '(libre)', a: answer });
      }
      const turn = buildTurn(state);
      return { state, turn };
    },

    toMagnifyContext(state, _locale) {
      const lines: string[] = [];
      lines.push('--- DISCOVERY (conversation Genesis <-> utilisateur, avant magnify) ---');
      lines.push(`Idee initiale : ${state.idea}`);
      lines.push('');
      lines.push('--- QUESTIONS & REPONSES ---');
      for (const ex of state.exchanges) {
        lines.push(`Q: ${ex.q}`);
        lines.push(`R: ${ex.a || '(pas de reponse)'}`);
        lines.push('');
      }
      lines.push('--- INSTRUCTIONS ---');
      lines.push(
        "Utilise ces reponses pour produire un manifest cale sur ce que veut vraiment l'utilisateur. " +
          'Les reponses priment sur tes hypotheses. Reponse absente -> decision la plus pragmatique, motivee brievement.',
      );
      return lines.join('\n');
    },
  };
}
